// The once-a-day anonymous "active" ping that counts how many people play.
// It is sent right after the day's first real action (a guess submitted),
// never on load, and carries only the game id, the platform and whether it is
// this browser's first ping of the ISO week. No identifier is sent or kept:
// the only stored values are <game>-active-day and <game>-active-week.
// The server counts pings; see mteur.com/services/activity.

export const ACTIVE_URL = "https://mteur.com/active";
export const PING_NONE = -1;
const DAY_MS = 86_400_000;
/** A clock earlier than this is a pinned test clock, not a real day. */
const EARLIEST_REAL_MS = Date.UTC(2026, 8, 1);

/** Days since 1970-01-01, UTC. */
export function utcDay(ms: number): number {
  return Math.floor(ms / DAY_MS);
}

/** The ISO week containing a day, as year*100 + week (2026-W39 is 202639). */
export function isoWeekKey(day: number): number {
  const weekday = (((day + 3) % 7) + 7) % 7; // Monday = 0
  const thursday = day - weekday + 3;
  const year = new Date(thursday * DAY_MS).getUTCFullYear();
  const week = Math.floor((thursday - utcDay(Date.UTC(year, 0, 1))) / 7) + 1;
  return year * 100 + week;
}

/** PING_NONE, 0 (a ping) or 1 (a ping that opens this browser's ISO week). */
export function pingDue(nowMs: number, lastDay: number | null, lastWeek: number | null, suppressed: boolean): number {
  if (suppressed || nowMs < EARLIEST_REAL_MS) return PING_NONE;
  const day = utcDay(nowMs);
  if (day === lastDay) return PING_NONE;
  return isoWeekKey(day) !== lastWeek ? 1 : 0;
}

export function pingBody(game: string, firstOfWeek: boolean): string {
  return JSON.stringify({ g: game, p: "web", w: firstOfWeek });
}

function readInt(key: string): number | null {
  const v = window.localStorage.getItem(key);
  if (v === null) return null;
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

let inFlight = false;

/** Call after a real game action. Sends the day's ping if it is due. */
export function notePlayed(game: string, suppressed = false, now = Date.now()): void {
  try {
    if (inFlight) return;
    const dayKey = game + "-active-day";
    const weekKey = game + "-active-week";
    const due = pingDue(now, readInt(dayKey), readInt(weekKey), suppressed);
    if (due === PING_NONE) return;
    const day = utcDay(now);
    const sent = fetch(ACTIVE_URL, {
      method: "POST",
      body: pingBody(game, due === 1),
      keepalive: true,
      headers: { "Content-Type": "application/json" },
    });
    inFlight = true;
    sent.then(
      (r) => {
        inFlight = false;
        if (r && r.ok) {
          try {
            window.localStorage.setItem(dayKey, String(day));
            window.localStorage.setItem(weekKey, String(isoWeekKey(day)));
          } catch (e) {}
        }
      },
      () => {
        inFlight = false;
      }
    );
  } catch (e) {
    inFlight = false;
  }
}
